import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Instagram, Facebook } from 'lucide-react';

const Navigation = ({ language = 'de', setLanguage, activeSection, setActiveSection, openBooking, currentPage, setCurrentPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const translations = {
    de: {
      hotel: 'Hotel & Restaurant La Casa',
      restaurant: 'Ristorante La Casa',
      hotelShort: 'Hotel',
      restaurantShort: 'Restaurant',
      bookRoom: 'Zimmer buchen',
      contact: 'Kontakt'
    },
    en: {
      hotel: 'Hotel & Restaurant La Casa',
      restaurant: 'Ristorante La Casa',
      hotelShort: 'Hotel',
      restaurantShort: 'Restaurant',
      bookRoom: 'Book a Room',
      contact: 'Contact'
    }
  };

  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    setLanguage && setLanguage(lng);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-luxury-cream backdrop-blur-xl shadow-2xl transition-all duration-500 overflow-x-hidden max-w-full">
      
      {/* DESKTOP Navigation */}
      <div className="hidden lg:flex items-center justify-between py-4 px-8 max-w-7xl mx-auto">
        
        {/* Logo lijevo */}
        <div className="flex items-center">
          <button
            onClick={() => setCurrentPage && setCurrentPage('home')}
            className="hover:scale-105 transition-transform duration-300"
          >
            <img 
              src="/logoHotela/Logo-la-casa-frei.png" 
              alt="Hotel & Restaurant La Casa" 
              className="h-14 w-auto object-contain"
            />
          </button>
        </div>

        {/* Dugme za prebacivanje Hotel/Restaurant */}
        <div className="flex items-center bg-luxury-charcoal rounded-full p-1.5 border border-luxury-gold/50">
          <button
            onClick={() => setActiveSection && setActiveSection('hotel')}
            className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-300 text-sm ${
              activeSection === 'hotel'
                ? 'bg-luxury-gold text-white shadow-lg'
                : 'text-white hover:bg-luxury-gold/20'
            }`}
          >
            {t.hotelShort}
          </button>
          <button
            onClick={() => setActiveSection && setActiveSection('restaurant')}
            className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-300 text-sm ${
              activeSection === 'restaurant'
                ? 'bg-luxury-gold text-white shadow-lg'
                : 'text-white hover:bg-luxury-gold/20'
            }`}
          >
            {t.restaurantShort}
          </button>
        </div>

        {/* Desna strana - Book Room, Social, Language, Contact */}
        <div className="flex items-center space-x-4">
          
          {/* Book a Room dugme */}
          <button
            onClick={() => openBooking && openBooking('room')}
            className="bg-luxury-gold hover:bg-luxury-darkGold text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg"
          >
            {t.bookRoom}
          </button>

          {/* Instagram i Facebook ikonice - luksuzni dizajn */}
          <div className="flex items-center space-x-3">
            <a 
              href="https://instagram.com/hausniggemann" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative flex items-center justify-center w-10 h-10 bg-luxury-charcoal/80 border border-luxury-gold/30 rounded-2xl backdrop-blur-sm hover:bg-luxury-gold/20 hover:border-luxury-gold transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-luxury-gold/30"
            >
              <Instagram size={18} className="text-luxury-gold group-hover:text-white transition-colors duration-300" />
              <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <a 
              href="https://facebook.com/hausniggemann" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative flex items-center justify-center w-10 h-10 bg-luxury-charcoal/80 border border-luxury-gold/30 rounded-2xl backdrop-blur-sm hover:bg-luxury-gold/20 hover:border-luxury-gold transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-luxury-gold/30"
            >
              <Facebook size={18} className="text-luxury-gold group-hover:text-white transition-colors duration-300" />
              <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
          </div>

          {/* Dugme za promjenu jezika */}
          <div className="flex items-center space-x-2 px-3 py-2 rounded-full bg-luxury-charcoal border border-luxury-gold/50 hover:border-luxury-gold transition-colors">
            <Globe className="text-luxury-gold" size={16} />
            <select
              onChange={(e) => changeLanguage(e.target.value)}
              value={language}
              className="bg-luxury-charcoal text-white text-sm focus:outline-none cursor-pointer"
            >
              <option value="en" className="bg-luxury-charcoal">EN</option>
              <option value="de" className="bg-luxury-charcoal">DE</option>
            </select>
          </div>

          {/* Contact dugme */}
          <button 
            onClick={() => setCurrentPage && setCurrentPage('contact')}
            className="bg-luxury-gold hover:bg-luxury-darkGold text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg"
          >
            {t.contact}
          </button>

        </div>
      </div>

      {/* MOBILE Navigation */}
      <div className="lg:hidden flex items-center justify-between py-2.5 px-2 w-full max-w-full box-border">
        
        {/* Logo - FIXED WIDTH */}
        <div className="flex items-center flex-shrink-0 w-14">
          <button
            onClick={() => setCurrentPage && setCurrentPage('home')}
            className="transition-opacity duration-300 w-full"
          >
            <img 
              src="/logoHotela/Logo-la-casa-frei.png" 
              alt="Hotel & Restaurant La Casa" 
              className="h-8 w-full object-contain"
            />
          </button>
        </div>

        {/* Hotel/Restaurant toggle za mobile - ABSOLUTE FIXED WIDTH */}
        <div className="flex items-center bg-luxury-charcoal/90 rounded-full p-0.5 border border-luxury-gold/50 h-9 flex-shrink-0" style={{width: '128px', minWidth: '128px', maxWidth: '128px'}}>
          <button
            onClick={() => setActiveSection && setActiveSection('hotel')}
            className={`rounded-full font-semibold transition-colors duration-200 text-[10px] h-7 flex items-center justify-center ${
              activeSection === 'hotel'
                ? 'bg-luxury-gold text-white'
                : 'text-white'
            }`}
            style={{width: '64px', minWidth: '64px', maxWidth: '64px'}}
          >
            {t.hotelShort}
          </button>
          <button
            onClick={() => setActiveSection && setActiveSection('restaurant')}
            className={`rounded-full font-semibold transition-colors duration-200 text-[10px] h-7 flex items-center justify-center ${
              activeSection === 'restaurant'
                ? 'bg-luxury-gold text-white'
                : 'text-white'
            }`}
            style={{width: '64px', minWidth: '64px', maxWidth: '64px'}}
          >
            {t.restaurantShort}
          </button>
        </div>

        {/* Right side - Language Switcher and Burger Menu */}
        <div className="flex items-center space-x-1 flex-shrink-0">
          {/* Mobile Language Switcher - FIXED WIDTH */}
          <div className="flex items-center px-2.5 py-1.5 rounded-full border border-luxury-gold bg-luxury-charcoal/90 backdrop-blur-sm h-9" style={{width: '68px', minWidth: '68px', maxWidth: '68px'}}>
            <Globe className="text-luxury-gold flex-shrink-0" size={13} />
            <select
              onChange={(e) => changeLanguage(e.target.value)}
              value={language}
              className="bg-transparent text-white text-[11px] font-semibold focus:outline-none cursor-pointer"
              style={{width: '36px'}}
            >
              <option value="en" className="bg-luxury-charcoal text-white">EN</option>
              <option value="de" className="bg-luxury-charcoal text-white">DE</option>
            </select>
          </div>

          {/* Burger Menu */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-luxury-charcoal p-2 hover:bg-luxury-gold/20 rounded-full transition-colors h-10 w-10 flex items-center justify-center"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-luxury-charcoal/98 backdrop-blur-lg border-t border-luxury-gold/20"
          >
            <div className="p-4 space-y-4">
              
              {/* Book Room */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openBooking && openBooking('room');
                }}
                className="w-full bg-luxury-gold hover:bg-luxury-darkGold text-white py-3 rounded-lg font-semibold transition-all duration-300"
              >
                {t.bookRoom}
              </button>

              {/* Social Icons */}
              <div className="flex items-center justify-center pt-2 border-t border-luxury-gold/20">
                <div className="flex items-center space-x-3">
                  <a 
                    href="https://instagram.com/hausniggemann" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-11 h-11 bg-luxury-slate/80 border border-luxury-gold/40 rounded-2xl backdrop-blur-sm hover:bg-luxury-gold/30 hover:border-luxury-gold transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-luxury-gold/40"
                  >
                    <Instagram size={20} className="text-luxury-gold group-hover:text-white transition-colors duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                  <a 
                    href="https://facebook.com/hausniggemann" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-11 h-11 bg-luxury-slate/80 border border-luxury-gold/40 rounded-2xl backdrop-blur-sm hover:bg-luxury-gold/30 hover:border-luxury-gold transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-luxury-gold/40"
                  >
                    <Facebook size={20} className="text-luxury-gold group-hover:text-white transition-colors duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                </div>
              </div>

              {/* Contact */}
              <button
                onClick={() => {
                  setCurrentPage && setCurrentPage('contact');
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-center border border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-white py-3 rounded-lg font-semibold transition-all duration-300"
              >
                {t.contact}
              </button>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </nav>
  );
};

export default Navigation;